import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useAuthStore } from '../store/authStore';
import { analytics } from '../hooks/useAnalytics';
import { formatLocalTime, isoToDatetimeLocal } from '../utils/time';
import { StatusBadge } from '../components/ui/StatusBadge';
import { CallButton } from '../components/ui/CallButton';
import { WhatsAppButton } from '../components/ui/WhatsAppButton';
import { AttributeChips } from '../components/ui/AttributeChips';
import { useTranslation } from '../i18n';
import { DirectionalIcon } from '../components/ui/DirectionalIcon';
import type { ParkingSpotAttributes } from '@lotngo/shared';
import { isNativePlatform } from '../lib/runtime';

// ─── types ────────────────────────────────────────────────────────────────────

interface Schedule {
  id: string;
  days_of_week: number[];
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface Window {
  id: string;
  type: 'free' | 'block';
  starts_at: string;
  ends_at: string | null;
  note: string | null;
  has_scheduled_claim?: boolean;
  has_open_change_request?: boolean;
}

interface SpotDetail {
  id: string;
  spot_number: string;
  level_code: string;
  level_label: string;
  description: string | null;
  status: 'free' | 'occupied' | 'unavailable' | 'claimed_for_future';
  auto_release_enabled?: boolean;
  attributes?: ParkingSpotAttributes;
  owner: { id: string; apartment: string };
  availability: { source: 'schedule' | 'window'; until: string } | null;
  active_session: {
    id: string;
    started_at: string;
    occupant: { id: string; apartment: string; phone?: string };
  } | null;
  scheduled_session: {
    id: string;
    scheduled_start_at: string;
    claimer: { apartment: string };
  } | null;
  schedules: Schedule[];
  upcoming_windows: Window[];
  pending_reclaim_alert: {
    id: string;
    sent_at: string;
    acknowledged_at: string | null;
    occupant_apartment: string;
  } | null;
}

// ─── helpers ──────────────────────────────────────────────────────────────────

const SCHEDULE_MESSAGES = [
  'My spot will no longer be available on a scheduled basis, I will need my spot soon',
  'My spot will no longer be available on a scheduled basis, you can stay until the end today',
] as const;

function getDays(t: (key: string) => string, language?: string) {
  const days = [
    { label: 'M', full: t('day_mon'), dayNum: 1 },
    { label: 'T', full: t('day_tue'), dayNum: 2 },
    { label: 'W', full: t('day_wed'), dayNum: 3 },
    { label: 'T', full: t('day_thu'), dayNum: 4 },
    { label: 'F', full: t('day_fri'), dayNum: 5 },
    { label: 'S', full: t('day_sat'), dayNum: 6 },
    { label: 'S', full: t('day_sun'), dayNum: 7 },
  ];

  // In Hebrew/Israel, show Saturday as the last day of the week
  if (language === 'he') {
    return [
      { label: 'א', full: t('day_sun'), dayNum: 7 },
      { label: 'ב', full: t('day_mon'), dayNum: 1 },
      { label: 'ג', full: t('day_tue'), dayNum: 2 },
      { label: 'ד', full: t('day_wed'), dayNum: 3 },
      { label: 'ה', full: t('day_thu'), dayNum: 4 },
      { label: 'ו', full: t('day_fri'), dayNum: 5 },
      { label: 'ש', full: t('day_sat'), dayNum: 6 },
    ];
  }

  return days;
}

function formatDays(days: number[], t: (key: string) => string, language?: string): string {
  if (!days.length) return '';
  const dayList = getDays(t, language);
  const dayMap = new Map(dayList.map(d => [d.dayNum, d.full]));
  const sorted = [...days].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0], end = sorted[0];
  for (let i = 1; i <= sorted.length; i++) {
    if (i < sorted.length && sorted[i] === end + 1) {
      end = sorted[i];
    } else {
      ranges.push(start === end ? dayMap.get(start)! : `${dayMap.get(start)}–${dayMap.get(end)}`);
      if (i < sorted.length) { start = sorted[i]; end = sorted[i]; }
    }
  }
  return ranges.join(', ');
}

// ─── SpotDetailPage ───────────────────────────────────────────────────────────

export default function SpotDetailPage() {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user     = useAuthStore((s) => s.user);
  const timezone = useAuthStore((s) => s.user?.building.timezone ?? 'UTC');
  const qc       = useQueryClient();
  const { t, language } = useTranslation();

  const [confirmCancelId, setConfirmCancelId]       = useState<string | null>(null);
  const [reclaimError,    setReclaimError]          = useState<string | null>(null);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [pendingClearId, setPendingClearId]         = useState<string | null>(null);
  const [sendingMessage, setSendingMessage]         = useState(false);
  const [deleteOpen,     setDeleteOpen]             = useState(false);
  const [deleteError,    setDeleteError]            = useState<string | null>(null);

  // Window-edit modal — shared between two modes:
  //   - 'edit'    : direct PATCH on the window (unclaimed case)
  //   - 'request' : files an availability_change_request (claimed case)
  // The form fields are identical; only the submit endpoint + title differ.
  const [changeReqWindow, setChangeReqWindow] = useState<Window | null>(null);
  const [changeReqMode,   setChangeReqMode]   = useState<'edit' | 'request'>('request');
  const [changeReqStart,  setChangeReqStart]  = useState('');
  const [changeReqEnd,    setChangeReqEnd]    = useState('');
  const [changeReqNote,   setChangeReqNote]   = useState('');
  const [changeReqError,  setChangeReqError]  = useState<string | null>(null);

  const { data: spot, isLoading, error } = useQuery({
    queryKey:             ['spot', id],
    queryFn:              () => apiClient.get<{ data: SpotDetail }>(`/spots/${id}`).then((r) => r.data.data),
    enabled:              !!id,
    refetchOnMount:       true,
    refetchOnWindowFocus: true,
    refetchInterval:      (query) => {
      const spotStatus = (query.state.data as SpotDetail | undefined)?.status;
      return spotStatus === 'occupied' || spotStatus === 'claimed_for_future' ? 15_000 : false;
    },
  });

  if (!isLoading && spot && spot.owner.id !== user?.id) {
    navigate('/spots', { replace: true });
    return null;
  }

  const sendReclaimMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/spots/${id}/reclaim-alert`, {
        message: 'I need my spot back.',
      }),
    onMutate: () => setReclaimError(null),
    onSuccess: () => {
      analytics.trackReclaimAlertSent(id!);
      setConfirmCancelId(null);
      setReclaimError(null);
      void qc.invalidateQueries({ queryKey: ['spot', id] });
      void qc.invalidateQueries({ queryKey: ['spots', 'mine'] });
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: { code?: string } } } })
        .response?.data?.error?.code;
      // Cooldown / already-pending mean the alert WAS sent — treat as success
      if (code === 'ALERT_COOLDOWN' || code === 'ALERT_ALREADY_PENDING') {
        setConfirmCancelId(null);
        setReclaimError(null);
        return;
      }
      switch (code) {
        case 'SPOT_NOT_OCCUPIED':     setReclaimError(t('reclaim_err_not_occupied'));    break;
        default:                      setReclaimError(t('reclaim_err_generic'));
      }
    },
  });

  const cancelWindowMutation = useMutation({
    mutationFn: (windowId: string) =>
      apiClient.delete(`/spots/${id}/windows/${windowId}`),
    onSuccess: () => {
      // Get the window type from spot data to track correctly
      const window = spot?.upcoming_windows.find((w) => w.id === confirmCancelId);
      analytics.trackAvailabilityWindowClosed(window?.type || 'free');
      setConfirmCancelId(null);
      void qc.invalidateQueries({ queryKey: ['spot', id] });
      void qc.invalidateQueries({ queryKey: ['spots', 'mine'] });
    },
  });

  const requestChangeMutation = useMutation({
    mutationFn: (payload: { window_id: string; starts: string; ends: string; note?: string }) =>
      apiClient.post('/availability-change-requests', {
        window_id:          payload.window_id,
        proposed_starts_at: new Date(payload.starts).toISOString(),
        proposed_ends_at:   new Date(payload.ends).toISOString(),
        owner_note:         payload.note?.trim() || undefined,
      }),
    onSuccess: () => {
      if (id) analytics.trackAvailabilityChangeRequested(id);
      setChangeReqWindow(null);
      setChangeReqStart('');
      setChangeReqEnd('');
      setChangeReqNote('');
      setChangeReqError(null);
      void qc.invalidateQueries({ queryKey: ['spot', id] });
      void qc.invalidateQueries({ queryKey: ['availability-change-requests', 'mine'] });
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: { code?: string } } } })
        ?.response?.data?.error?.code;
      setChangeReqError(code ? t(`change_req_err_${code.toLowerCase()}`) || code : t('error_try_again'));
    },
  });

  function openChangeRequest(w: Window) {
    setChangeReqMode('request');
    setChangeReqWindow(w);
    setChangeReqStart(isoToDatetimeLocal(w.starts_at, timezone));
    setChangeReqEnd(w.ends_at ? isoToDatetimeLocal(w.ends_at, timezone) : '');
    setChangeReqNote(w.note ?? '');
    setChangeReqError(null);
  }

  function openEditWindow(w: Window) {
    setChangeReqMode('edit');
    setChangeReqWindow(w);
    setChangeReqStart(isoToDatetimeLocal(w.starts_at, timezone));
    setChangeReqEnd(w.ends_at ? isoToDatetimeLocal(w.ends_at, timezone) : '');
    setChangeReqNote(w.note ?? '');
    setChangeReqError(null);
  }

  const editWindowMutation = useMutation({
    mutationFn: (payload: { window_id: string; starts: string; ends: string; note?: string }) =>
      apiClient.patch(`/spots/${id}/windows/${payload.window_id}`, {
        starts_at: new Date(payload.starts).toISOString(),
        ends_at:   new Date(payload.ends).toISOString(),
        note:      payload.note?.trim() || undefined,
      }),
    onSuccess: (_res, payload) => {
      analytics.trackAvailabilityWindowEdited(payload.window_id);
      setChangeReqWindow(null);
      setChangeReqStart('');
      setChangeReqEnd('');
      setChangeReqNote('');
      setChangeReqError(null);
      void qc.invalidateQueries({ queryKey: ['spot', id] });
      void qc.invalidateQueries({ queryKey: ['spots', 'mine'] });
      void qc.invalidateQueries({ queryKey: ['spots', 'free'] });
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: { code?: string } } } })
        ?.response?.data?.error?.code;
      setChangeReqError(code ? t(`change_req_err_${code.toLowerCase()}`) || code : t('error_try_again'));
    },
  });

  const clearScheduleMutation = useMutation({
    mutationFn: (scheduleId: string) =>
      apiClient.delete(`/spots/${id}/schedules/${scheduleId}`),
    onSuccess: () => {
      analytics.trackScheduleDeleted();
      setShowScheduleDialog(false);
      setPendingClearId(null);
      void qc.invalidateQueries({ queryKey: ['spot', id] });
      void qc.invalidateQueries({ queryKey: ['spots', 'mine'] });
    },
  });

  // Per-spot Smart Auto-Release opt-in. Owners with multiple spots can flip
  // an individual spot off so only their "morning commute" spot is touched
  // by the geofence trigger. Server filter lives in autoReleaseService.
  const autoReleaseToggleMutation = useMutation({
    mutationFn: (next: boolean) =>
      apiClient.patch(`/spots/${id}`, { auto_release_enabled: next }),
    onSuccess: (_res, next) => {
      qc.setQueryData<SpotDetail | undefined>(['spot', id], (prev) =>
        prev ? { ...prev, auto_release_enabled: next } : prev,
      );
    },
  });

  // Moved from MySpotsPage — delete here keeps the action away from the
  // browse-list card (where accidental taps were a real problem) and behind
  // a deliberate red button at the bottom of the detail page.
  const deleteSpotMutation = useMutation({
    mutationFn: () => apiClient.delete(`/spots/${id}`),
    onSuccess: () => {
      analytics.trackSpotDeleted(id!);
      setDeleteOpen(false);
      void qc.invalidateQueries({ queryKey: ['spots', 'mine'] });
      void qc.invalidateQueries({ queryKey: ['spots', 'mine', 'disabled'] });
      void qc.invalidateQueries({ queryKey: ['spots', 'free'] });
      navigate('/spots', { replace: true });
    },
    onError: (err: unknown) => {
      setDeleteOpen(false);
      const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
      if (code === 'SPOT_HAS_ACTIVE_SESSION') {
        setDeleteError(t('delete_error_active_session') || 'This spot has an active session. Wait for the occupant to leave before removing it.');
      } else if (code === 'FORBIDDEN') {
        setDeleteError(t('delete_error_forbidden') || 'You do not have permission to remove this spot.');
      } else {
        setDeleteError(t('delete_error_generic') || 'Something went wrong. Please try again.');
      }
    },
  });

  const handleClearSchedule = () => {
    const activeSchedules = spot?.schedules.filter((s) => s.is_active) || [];
    if (activeSchedules.length === 0) return;

    if (spot?.status === 'occupied' && spot.active_session) {
      setPendingClearId(activeSchedules.map((s) => s.id).join(','));
      setShowScheduleDialog(true);
    } else {
      activeSchedules.forEach((schedule) => {
        clearScheduleMutation.mutate(schedule.id);
      });
    }
  };

  const handleScheduleMessage = async (message: string) => {
    setSendingMessage(true);
    try {
      // Send reclaim alert with message instead of just notification
      // This creates a traceable alert that the occupant can acknowledge
      await apiClient.post(`/spots/${id}/reclaim-alert`, { message });
    } catch {
      // Alert may already be pending — ignore
    } finally {
      setSendingMessage(false);
    }
    if (pendingClearId) {
      const scheduleIds = pendingClearId.split(',');
      scheduleIds.forEach((scheduleId) => {
        clearScheduleMutation.mutate(scheduleId);
      });
    }
  };

  const handleScheduleDialogSkip = () => {
    if (pendingClearId) clearScheduleMutation.mutate(pendingClearId);
    else setShowScheduleDialog(false);
  };

  // ── loading / error ────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div style={{ ...page, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#9ca3af' }}>{t('loading')}</div>
      </div>
    );
  }

  if (error || !spot) {
    return (
      <div style={{ ...page, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#dc2626' }}>{t('spot_not_found')}</div>
      </div>
    );
  }

  // Track spot details view
  if (typeof window !== 'undefined') {
    analytics.trackSpotDetailsViewed(spot.id, spot.level_code);
  }

  // ── derived state ──────────────────────────────────────────────────────────

  const spotLabel       = `${t(spot.level_code) || spot.level_label} · ${spot.spot_number}`;
  const isOccupied      = spot.status === 'occupied';
  const isClaimedForFuture = spot.status === 'claimed_for_future';
  const activeWindow    = spot.upcoming_windows.find(
    (w) => w.type === 'free' && new Date(w.starts_at) <= new Date() && (!w.ends_at || new Date(w.ends_at) > new Date()),
  );
  const activeSchedule = spot.schedules.find((s) => s.is_active);

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="page" style={{ maxWidth: '430px', margin: '0 auto' }}>

      {/* ── Fixed topbar ── */}
      <header className="topbar pt-[env(safe-area-inset-top,0px)] h-[calc(64px+env(safe-area-inset-top,0px))]" style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        //height: '64px',
        padding: '0 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 12px 32px -4px rgba(43,52,55,0.06)',
      }}>
        <button
          onClick={() => navigate('/spots')}
          style={iconBtn}
          aria-label="Back to My Spots"
        >
          <DirectionalIcon icon="arrow_back" style={{ fontSize: '24px', color: '#005bc0' }} />
        </button>

        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 114 26"
            style={{ height: '26px', width: 'auto', display: 'block' }}
            aria-label="Lot'N'Go"
          >
            <text
              x="57" y="20"
              fontFamily="Manrope, system-ui, sans-serif"
              fontWeight="700"
              fontSize="20"
              fill="#005bc0"
              textAnchor="middle"
              letterSpacing="-0.4"
            >
              {"Lot'N'Go"}
            </text>
          </svg>
        </div>

        <div style={{ width: '40px', flexShrink: 0 }} />
      </header>

      {/* ── Scrollable body ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '80px 20px 48px' }}>

        {/* ── Title ── */}
        <section style={{ marginBottom: '20px' }}>
          <h2 style={{
            fontFamily:    "'Manrope', system-ui, sans-serif",
            fontSize:      '28px',
            fontWeight:    800,
            color:         '#2b3437',
            letterSpacing: '-0.02em',
            margin:        0,
            lineHeight:    1.15,
          }}>
            {spotLabel}
          </h2>
          {spot.description && (
            <p style={{ fontSize: '14px', color: '#586064', margin: '6px 0 0', lineHeight: 1.5 }}>
              {spot.description}
            </p>
          )}
        </section>

        {/* ── Status & Ownership Card ── */}
        <section style={{
          background:   '#ffffff',
          borderRadius: '16px',
          padding:      '20px',
          boxShadow:    '0 12px 32px -4px rgba(43,52,55,0.06)',
          marginBottom: '16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div>
              <StatusBadge status={spot.status} />
              <p style={{ fontSize: '13px', color: '#586064', margin: '6px 0 0', fontWeight: 500 }}>
                {isOccupied
                  ? t('apt_using_it', { apartment: spot.active_session?.occupant.apartment ?? '?' })
                  : isClaimedForFuture && spot.scheduled_session
                  ? t('apt_claimed_for_future', { apartment: spot.scheduled_session.claimer.apartment })
                  : t('no_one_using')}
              </p>
            </div>
            <div style={{
              width:          '48px',
              height:         '48px',
              borderRadius:   '50%',
              background:     '#eaeff1',
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              flexShrink:     0,
            }}>
              <span className="material-symbols-outlined" style={{ color: '#737c7f', fontSize: '24px' }}>garage</span>
            </div>
          </div>

          <div style={{ borderTop: '1px solid rgba(43,52,55,0.07)', paddingTop: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width:          '32px',
              height:         '32px',
              borderRadius:   '50%',
              background:     '#d8e2ff',
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              flexShrink:     0,
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: '18px', color: '#005bc0' }}>person</span>
            </div>
            <span style={{
              fontFamily: "'Manrope', system-ui, sans-serif",
              fontWeight: 600,
              color:      '#2b3437',
              fontSize:   '14px',
            }}>
              {t('apt_spot', { apartment: spot.owner.apartment })}
            </span>
          </div>
          <AttributeChips attributes={spot.attributes} />
        </section>

        {/* ── Occupied: active window cancel ── */}
        {isOccupied && spot.active_session && (
          <section style={{
            background:   '#fefce8',
            borderRadius: '16px',
            padding:      '16px 20px',
            marginBottom: '16px',
          }}>
            <div style={{ fontWeight: 600, color: '#92400e', fontSize: '14px' }}>
              {t('apt_using_your_spot', { apartment: spot.active_session.occupant.apartment })}
            </div>
            <div style={{ fontSize: '12px', color: '#b45309', marginTop: '2px', marginBottom: '14px' }}>
              {t('since')} {formatLocalTime(spot.active_session.started_at, timezone, 'time')}
            </div>

            {spot.active_session.occupant.phone && (
              <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                <div style={{ flex: 1 }}>
                  <CallButton
                    occupantPhone={spot.active_session.occupant.phone}
                    occupantId={spot.active_session.occupant.id}
                    spotLabel={spotLabel}
                    variant="button"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <WhatsAppButton
                    occupantPhone={spot.active_session.occupant.phone}
                    occupantId={spot.active_session.occupant.id}
                    spotLabel={spotLabel}
                    variant="button"
                  />
                </div>
              </div>
            )}

            {confirmCancelId !== 'reclaim' ? (
              <button
                onClick={() => setConfirmCancelId('reclaim')}
                style={{ ...actionBtn, background: 'rgba(254,242,242,0.8)', color: '#dc2626' }}
              >
                {t('send_reclaim_alert')}
              </button>
            ) : (
              <div style={{ background: 'rgba(254,242,242,0.8)', borderRadius: '12px', padding: '14px' }}>
                <div style={{ fontSize: '13px', color: '#dc2626', marginBottom: '10px', fontWeight: 500 }}>
                  {activeWindow ? t('window_cancel_info') : t('reclaim_confirmation_message')}
                </div>
                {reclaimError && (
                  <div style={{
                    background:   '#fef2f2',
                    border:       '1px solid #fecaca',
                    borderRadius: '8px',
                    padding:      '10px 12px',
                    fontSize:     '12px',
                    color:        '#991b1b',
                    fontWeight:   500,
                    marginBottom: '10px',
                  }}>
                    {reclaimError}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => sendReclaimMutation.mutate()}
                    disabled={sendReclaimMutation.isPending}
                    style={{ ...smallBtn, background: '#dc2626', color: '#fff', flex: 1 }}
                  >
                    {sendReclaimMutation.isPending ? t('sending') : t('confirm')}
                  </button>
                  <button
                    onClick={() => { setConfirmCancelId(null); setReclaimError(null); }}
                    style={{ ...smallBtn, background: '#f3f4f6', color: '#374151', flex: 1 }}
                  >
                    {t('keep_it')}
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* ── Claimed for Future ── */}
        {isClaimedForFuture && spot.scheduled_session && (
          <section style={{
            background:   '#dbeafe',
            borderRadius: '16px',
            padding:      '16px 20px',
            marginBottom: '16px',
          }}>
            <div style={{ fontWeight: 600, color: '#1e40af', fontSize: '14px' }}>
              {t('apt_claimed_this_spot', { apartment: spot.scheduled_session.claimer.apartment })}
            </div>
            <div style={{ fontSize: '12px', color: '#1e3a8a', marginTop: '2px' }}>
              {t('starts')} {formatLocalTime(spot.scheduled_session.scheduled_start_at, timezone, 'datetime')}
            </div>
          </section>
        )}

        {/* ── Smart Auto-Release per-spot toggle ──
            Owners with multiple spots can opt one out without disabling the
            master setting in Profile. Hidden when:
              - the spot has no auto_release_enabled field (older API), OR
              - we're not running inside the Capacitor native shell — same
                gate as the master panel in ProfilePage. The web PWA has no
                background-location story so the feature is meaningless
                there; surfacing a toggle would mislead the user. */}
        {isNativePlatform() && spot.auto_release_enabled !== undefined && (
          <div style={{
            display:        'flex',
            justifyContent: 'space-between',
            alignItems:     'center',
            gap:            '12px',
            width:          '100%',
            boxSizing:      'border-box',
            // Match the dominant card style on this page (Claimed-for-Future
            // section, info banners): no border, soft shadow, 16/20 padding,
            // 16 radius. The previous bordered look made this row visibly
            // narrower than the siblings because the 1px border ate into
            // the inner width.
            padding:        '16px 20px',
            borderRadius:   '16px',
            border:         'none',
            background:     '#ffffff',
            boxShadow:      '0 4px 16px -4px rgba(43,52,55,0.05)',
            marginBottom:   '12px',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: "'Manrope', system-ui, sans-serif", fontWeight: 600, fontSize: '14px', color: '#1f2937' }}>
                {t('auto_release_spot_label')}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', lineHeight: 1.4 }}>
                {t('auto_release_spot_desc')}
              </div>
            </div>
            <button
              role="switch"
              aria-checked={!!spot.auto_release_enabled}
              disabled={autoReleaseToggleMutation.isPending}
              onClick={() => autoReleaseToggleMutation.mutate(!spot.auto_release_enabled)}
              style={{
                position:     'relative',
                width:        '44px',
                height:       '26px',
                borderRadius: '13px',
                border:       'none',
                flexShrink:   0,
                cursor:       autoReleaseToggleMutation.isPending ? 'not-allowed' : 'pointer',
                background:   spot.auto_release_enabled ? '#16a34a' : '#d1d5db',
                opacity:      autoReleaseToggleMutation.isPending ? 0.6 : 1,
                transition:   'background-color 0.2s',
                padding:      0,
              }}
            >
              <span style={{
                position:        'absolute',
                top:             '2px',
                left:            spot.auto_release_enabled ? '20px' : '2px',
                width:           '22px',
                height:          '22px',
                borderRadius:    '50%',
                background:      '#fff',
                boxShadow:       '0 1px 3px rgba(0,0,0,0.3)',
                transition:      'left 0.2s',
              }} />
            </button>
          </div>
        )}

        {/* ── Edit parking details (attributes) ── */}
        <button
          type="button"
          onClick={() => navigate(`/spots/${id}/attributes`)}
          style={{
            width:        '100%',
            padding:      '12px 16px',
            borderRadius: '12px',
            border:       '1px solid #e5e7eb',
            background:   '#ffffff',
            display:      'flex',
            alignItems:   'center',
            justifyContent:'space-between',
            cursor:       'pointer',
            marginBottom: '12px',
          }}
        >
          <span style={{ fontFamily: "'Manrope', system-ui, sans-serif", fontWeight: 600, fontSize: '14px', color: '#1f2937' }}>
            {t('attr_edit_title')}
          </span>
          <DirectionalIcon icon="chevron_right" />
        </button>

        {/* ── Mark free CTA ── */}
        <button
          onClick={() => navigate(`/spots/${id}/free`)}
          style={{
            width:          '100%',
            padding:        '18px 20px',
            borderRadius:   '14px',
            border:         '1px solid #bbf7d0',
            background:     '#f0fdf4',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            gap:            '10px',
            cursor:         'pointer',
            marginBottom:   '16px',
            transition:     'background 0.15s',
          }}
        >
          <span className="material-symbols-outlined" style={{ color: '#16a34a', fontSize: '22px' }}>add_circle</span>
          <span style={{
            fontFamily: "'Manrope', system-ui, sans-serif",
            fontWeight: 700,
            fontSize:   '15px',
            color:      '#15803d',
          }}>
            {t('mark_free_or_add_window')}
          </span>
        </button>

        {/* ── Recurring Schedule ── */}
        <section style={sectionCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <span style={sectionLabel}>{t('recurring_schedule')}</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => navigate(`/spots/${id}/schedule`)}
                style={primaryChipBtn}
              >
                {activeSchedule ? t('edit') : t('set_up')}
              </button>
              {activeSchedule && (
                <button
                  onClick={() => handleClearSchedule()}
                  disabled={clearScheduleMutation.isPending}
                  style={dangerChipBtn}
                >
                  {clearScheduleMutation.isPending ? '…' : t('clear')}
                </button>
              )}
            </div>
          </div>

          {activeSchedule ? (() => {
            const activeSchedules = spot.schedules.filter((s) => s.is_active);

            // Group schedules by their time and display them all
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {activeSchedules.map((schedule) => (
                  <div key={schedule.id} style={infoCard}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#2b3437', marginBottom: '2px' }}>
                      {formatDays(schedule.days_of_week, t, language)}
                    </div>
                    <div style={{ fontSize: '13px', color: '#586064' }}>
                      {schedule.start_time} – {schedule.end_time}
                    </div>
                  </div>
                ))}
                {!activeSchedule.is_active && (
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('inactive')}</div>
                )}
              </div>
            );
          })() : (
            <div style={dashedEmpty}>
              <span className="material-symbols-outlined" style={{ fontSize: '32px', color: '#abb3b7', marginBottom: '6px' }}>event_repeat</span>
              <p style={{ fontSize: '13px', color: '#737c7f', fontStyle: 'italic', margin: 0 }}>{t('no_schedule_set')}</p>
            </div>
          )}
        </section>

        {/* ── One-off Windows ── */}
        <section style={sectionCard}>
          <span style={{ ...sectionLabel, display: 'block', marginBottom: '16px' }}>{t('oneoff_windows')}</span>

          {spot.upcoming_windows.length > 0 ? (
            spot.upcoming_windows.map((w) => {
              const isActiveNow = w.type === 'free'
                && new Date(w.starts_at) <= new Date()
                && (!w.ends_at || new Date(w.ends_at) > new Date());
              const confirming = confirmCancelId === w.id;

              return (
                <div key={w.id} style={{ ...infoCard, marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      fontSize:        '11px',
                      fontWeight:      600,
                      padding:         '2px 8px',
                      borderRadius:    '9999px',
                      backgroundColor: w.type === 'free' ? '#dcfce7' : '#fee2e2',
                      color:           w.type === 'free' ? '#15803d' : '#dc2626',
                    }}>
                      {w.type.toUpperCase()}
                    </span>
                    <span style={{ fontSize: '13px', color: '#374151', flex: 1 }}>
                      {formatLocalTime(w.starts_at, timezone, 'time')}
                      {w.ends_at ? ` – ${formatLocalTime(w.ends_at, timezone, 'time')}` : ''}
                    </span>
                    {w.type === 'free' && !confirming && (
                      w.has_scheduled_claim ? (
                        w.has_open_change_request ? (
                          <span style={{
                            background:   '#fff7ed',
                            border:       '1px solid #fdba74',
                            borderRadius: '8px',
                            color:        '#c2410c',
                            fontSize:     '11px',
                            fontWeight:   600,
                            padding:      '4px 10px',
                            flexShrink:   0,
                          }}>
                            {t('change_request_pending')}
                          </span>
                        ) : (
                          <button
                            onClick={() => openChangeRequest(w)}
                            style={{
                              background:   '#eff6ff',
                              border:       'none',
                              borderRadius: '8px',
                              color:        '#1d4ed8',
                              fontSize:     '12px',
                              fontWeight:   600,
                              cursor:       'pointer',
                              padding:      '4px 10px',
                              flexShrink:   0,
                            }}
                          >
                            {t('request_change')}
                          </button>
                        )
                      ) : (
                        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                          <button
                            onClick={() => openEditWindow(w)}
                            style={{
                              background:   '#eff6ff',
                              border:       'none',
                              borderRadius: '8px',
                              color:        '#1d4ed8',
                              fontSize:     '12px',
                              fontWeight:   600,
                              cursor:       'pointer',
                              padding:      '4px 10px',
                            }}
                          >
                            {t('edit_window')}
                          </button>
                          <button
                            onClick={() => setConfirmCancelId(w.id)}
                            style={{
                              background:   '#fef2f2',
                              border:       'none',
                              borderRadius: '8px',
                              color:        '#dc2626',
                              fontSize:     '12px',
                              fontWeight:   600,
                              cursor:       'pointer',
                              padding:      '4px 10px',
                            }}
                          >
                            {t('clear')}
                          </button>
                        </div>
                      )
                    )}
                  </div>
                  {w.note && (
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>{w.note}</div>
                  )}
                  {confirming && (
                    <div style={{ marginTop: '12px', padding: '12px', borderRadius: '10px', background: '#fef2f2' }}>
                      <div style={{ fontSize: '13px', color: '#dc2626', marginBottom: '8px', fontWeight: 500 }}>
                        {t('cancel_free_window')}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => cancelWindowMutation.mutate(w.id)}
                          disabled={cancelWindowMutation.isPending}
                          style={{ ...smallBtn, background: '#dc2626', color: '#fff' }}
                        >
                          {cancelWindowMutation.isPending ? t('cancelling') : t('confirm')}
                        </button>
                        <button
                          onClick={() => setConfirmCancelId(null)}
                          style={{ ...smallBtn, background: '#f3f4f6', color: '#374151' }}
                        >
                          {t('keep_it')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div style={dashedEmpty}>
              <span className="material-symbols-outlined" style={{ fontSize: '32px', color: '#abb3b7', marginBottom: '6px' }}>calendar_today</span>
              <p style={{ fontSize: '13px', color: '#737c7f', fontStyle: 'italic', margin: 0 }}>{t('no_upcoming_windows')}</p>
            </div>
          )}
        </section>

        {/* Delete spot — destructive action, intentionally at the bottom of the
            page and behind a confirm dialog. Disabled when the spot is occupied;
            user must wait for the session to end first. */}
        <section style={{ marginTop: '32px', paddingTop: '16px', borderTop: '1px solid rgba(43,52,55,0.08)' }}>
          <button
            onClick={() => setDeleteOpen(true)}
            disabled={spot.status === 'occupied'}
            title={spot.status === 'occupied' ? t('end_session_first') : undefined}
            style={{
              width:        '100%',
              height:       '48px',
              borderRadius: '12px',
              border:       'none',
              background:   spot.status === 'occupied' ? '#fca5a5' : '#dc2626',
              color:        '#ffffff',
              fontSize:     '14px',
              fontWeight:   700,
              cursor:       spot.status === 'occupied' ? 'not-allowed' : 'pointer',
              opacity:      spot.status === 'occupied' ? 0.6 : 1,
              display:      'flex',
              alignItems:   'center',
              justifyContent: 'center',
              gap:          '8px',
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>delete</span>
            {t('remove_spot_action')}
          </button>
          {spot.status === 'occupied' && (
            <p style={{ fontSize: '12px', color: 'var(--on-surface-3)', textAlign: 'center', marginTop: '8px' }}>
              {t('end_session_first')}
            </p>
          )}
        </section>

      </div>

      {/* ── Edit / Request-change modal (shared shape) ── */}
      {changeReqWindow && (() => {
        const isEdit = changeReqMode === 'edit';
        const activeMutation = isEdit ? editWindowMutation : requestChangeMutation;
        return (
        <div
          style={dialogOverlay}
          onClick={(e) => {
            if (e.target === e.currentTarget && !activeMutation.isPending) {
              setChangeReqWindow(null);
            }
          }}
        >
          <div style={{ ...dialogBox, maxWidth: '380px' }}>
            <div style={{
              fontWeight: 700, fontSize: '17px', color: '#2b3437',
              marginBottom: '4px', fontFamily: "'Manrope', system-ui, sans-serif",
            }}>
              {t(isEdit ? 'edit_window_title' : 'request_change_title')}
            </div>
            <div style={{ fontSize: '13px', color: '#586064', marginBottom: '16px', lineHeight: 1.5 }}>
              {t(isEdit ? 'edit_window_subtitle' : 'request_change_subtitle')}
            </div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
              {t('new_start')}
            </label>
            <input
              type="datetime-local"
              value={changeReqStart}
              onChange={(e) => setChangeReqStart(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px', borderRadius: '10px',
                border: '1px solid #d1d5db', fontSize: '14px', marginBottom: '12px',
              }}
            />
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
              {t('new_end')}
            </label>
            <input
              type="datetime-local"
              value={changeReqEnd}
              onChange={(e) => setChangeReqEnd(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px', borderRadius: '10px',
                border: '1px solid #d1d5db', fontSize: '14px', marginBottom: '12px',
              }}
            />
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
              {t('note_optional')}
            </label>
            <textarea
              value={changeReqNote}
              onChange={(e) => setChangeReqNote(e.target.value)}
              maxLength={500}
              rows={3}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px', borderRadius: '10px',
                border: '1px solid #d1d5db', fontSize: '14px', marginBottom: '12px',
                resize: 'vertical',
              }}
            />
            {changeReqError && (
              <div style={{
                fontSize: '13px', color: '#dc2626', marginBottom: '12px',
                padding: '8px 10px', background: '#fef2f2', borderRadius: '8px',
              }}>
                {changeReqError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setChangeReqWindow(null)}
                disabled={activeMutation.isPending}
                style={{
                  flex: 1, padding: '10px', borderRadius: '10px',
                  border: '1px solid #d1d5db', background: '#ffffff',
                  color: '#374151', fontWeight: 600, fontSize: '14px', cursor: 'pointer',
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => {
                  if (!changeReqStart || !changeReqEnd) {
                    setChangeReqError(t('change_req_err_change_range_invalid'));
                    return;
                  }
                  const payload = {
                    window_id: changeReqWindow.id,
                    starts:    changeReqStart,
                    ends:      changeReqEnd,
                    note:      changeReqNote,
                  };
                  if (isEdit) editWindowMutation.mutate(payload);
                  else        requestChangeMutation.mutate(payload);
                }}
                disabled={activeMutation.isPending}
                style={{
                  flex: 1, padding: '10px', borderRadius: '10px',
                  border: 'none', background: '#005bc0', color: '#ffffff',
                  fontWeight: 700, fontSize: '14px', cursor: 'pointer',
                }}
              >
                {activeMutation.isPending ? '…' : t(isEdit ? 'save_changes' : 'send_request')}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── Schedule change / clear dialog ── */}
      {showScheduleDialog && (
        <div style={dialogOverlay}>
          <div style={dialogBox}>
            <div style={{
              fontWeight:  700,
              fontSize:    '16px',
              color:       '#2b3437',
              marginBottom: '6px',
              fontFamily:  "'Manrope', system-ui, sans-serif",
            }}>
              {t('notify_occupant')}
            </div>
            <div style={{ fontSize: '13px', color: '#586064', marginBottom: '20px', lineHeight: 1.5 }}>
              {t('occupant_using_spot', { apartment: spot.active_session?.occupant.apartment })}
            </div>

            {SCHEDULE_MESSAGES.map((msg) => (
              <button
                key={msg}
                onClick={() => handleScheduleMessage(msg)}
                disabled={sendingMessage || clearScheduleMutation.isPending}
                style={msgBtn}
              >
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#2b3437', display: 'block', marginBottom: '2px' }}>
                  {msg.includes('need my spot soon') ? t('need_spot_soon') : t('stay_until_end_of_day')}
                </span>
                <span style={{ fontSize: '12px', color: '#586064', fontStyle: 'italic' }}>
                  "{msg}"
                </span>
              </button>
            ))}

            <button
              onClick={handleScheduleDialogSkip}
              disabled={sendingMessage || clearScheduleMutation.isPending}
              style={{ ...msgBtn, background: '#f1f4f6', marginTop: '4px' }}
            >
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#586064' }}>
                {clearScheduleMutation.isPending ? t('clearing') : t('skip_notify')}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Delete confirm dialog */}
      {deleteOpen && (
        <>
          <div
            onClick={() => !deleteSpotMutation.isPending && setDeleteOpen(false)}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 300 }}
          />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 'calc(100% - 48px)', maxWidth: '320px',
            backgroundColor: '#ffffff', borderRadius: '20px', padding: '28px 20px 20px',
            zIndex: 301, boxShadow: '0 16px 40px -4px rgba(43,52,55,0.18)',
          }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '14px',
              background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <span className="material-symbols-outlined" style={{ color: '#dc2626', fontSize: '24px' }}>delete</span>
            </div>
            <div style={{
              fontFamily: 'var(--manrope)', fontSize: '17px', fontWeight: 700,
              textAlign: 'center', marginBottom: '8px', color: 'var(--on-surface)',
            }}>
              {t('remove_spot')}
            </div>
            <div style={{
              fontSize: '13px', color: 'var(--on-surface-2)',
              textAlign: 'center', marginBottom: '24px', lineHeight: 1.5,
            }}>
              <strong>{spotLabel}</strong> {t('remove_message')}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setDeleteOpen(false)}
                disabled={deleteSpotMutation.isPending}
                style={{
                  flex: 1, height: '44px', borderRadius: '10px',
                  border: '1.5px solid var(--primary)', background: '#ffffff',
                  color: 'var(--primary)', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                }}
              >
                {t('no_keep_it')}
              </button>
              <button
                onClick={() => deleteSpotMutation.mutate()}
                disabled={deleteSpotMutation.isPending}
                style={{
                  flex: 1, height: '44px', borderRadius: '10px', border: 'none',
                  background: '#dc2626', color: '#ffffff', fontSize: '14px', fontWeight: 600,
                  cursor: deleteSpotMutation.isPending ? 'not-allowed' : 'pointer',
                  opacity: deleteSpotMutation.isPending ? 0.7 : 1,
                }}
              >
                {deleteSpotMutation.isPending ? t('removing') : t('yes_remove')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Delete error dialog */}
      {deleteError && (
        <>
          <div onClick={() => setDeleteError(null)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 300 }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 'calc(100% - 48px)', maxWidth: '320px',
            backgroundColor: '#ffffff', borderRadius: '20px', padding: '24px 20px',
            zIndex: 301, boxShadow: '0 16px 40px -4px rgba(43,52,55,0.18)',
          }}>
            <div style={{ fontSize: '14px', color: 'var(--on-surface)', textAlign: 'center', marginBottom: '20px', lineHeight: 1.5 }}>
              {deleteError}
            </div>
            <button
              onClick={() => setDeleteError(null)}
              style={{
                width: '100%', height: '44px', borderRadius: '10px',
                border: 'none', background: 'var(--primary)', color: '#ffffff',
                fontSize: '14px', fontWeight: 600, cursor: 'pointer',
              }}
            >
              {t('ok') || 'OK'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── styles ───────────────────────────────────────────────────────────────────

const page: React.CSSProperties = {
  display:         'flex',
  flexDirection:   'column',
  minHeight:       '100dvh',
  maxWidth:        '430px',
  margin:          '0 auto',
  backgroundColor: '#f8f9fa',
  fontFamily:      "'Inter', system-ui, -apple-system, sans-serif",
  position:        'relative',
};

const iconBtn: React.CSSProperties = {
  width:          '40px',
  height:         '40px',
  border:         'none',
  background:     'none',
  cursor:         'pointer',
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'center',
  borderRadius:   '9999px',
  flexShrink:     0,
};

const sectionCard: React.CSSProperties = {
  background:   '#f1f4f6',
  borderRadius: '16px',
  padding:      '20px',
  marginBottom: '16px',
};

const sectionLabel: React.CSSProperties = {
  fontSize:      '11px',
  fontWeight:    700,
  letterSpacing: '0.15em',
  color:         '#737c7f',
  textTransform: 'uppercase',
};

const primaryChipBtn: React.CSSProperties = {
  background:   '#005bc0',
  color:        '#f7f7ff',
  border:       'none',
  borderRadius: '8px',
  padding:      '6px 14px',
  fontSize:     '12px',
  fontWeight:   700,
  cursor:       'pointer',
};

const dangerChipBtn: React.CSSProperties = {
  background:   '#fef2f2',
  color:        '#dc2626',
  border:       '1px solid #fecaca',
  borderRadius: '8px',
  padding:      '6px 14px',
  fontSize:     '12px',
  fontWeight:   700,
  cursor:       'pointer',
};

const dashedEmpty: React.CSSProperties = {
  display:        'flex',
  flexDirection:  'column',
  alignItems:     'center',
  justifyContent: 'center',
  padding:        '32px 16px',
  border:         '2px dashed rgba(171,179,183,0.4)',
  borderRadius:   '12px',
  textAlign:      'center',
};

const infoCard: React.CSSProperties = {
  background:   '#ffffff',
  borderRadius: '12px',
  padding:      '14px 16px',
  boxShadow:    '0 2px 8px rgba(43,52,55,0.05)',
};

const actionBtn: React.CSSProperties = {
  display:      'block',
  width:        '100%',
  height:       '44px',
  borderRadius: '12px',
  border:       'none',
  color:        '#fff',
  fontSize:     '14px',
  fontWeight:   600,
  cursor:       'pointer',
};

const smallBtn: React.CSSProperties = {
  height:       '34px',
  padding:      '0 16px',
  borderRadius: '8px',
  border:       'none',
  fontSize:     '13px',
  fontWeight:   600,
  cursor:       'pointer',
};

const dialogOverlay: React.CSSProperties = {
  position:       'fixed',
  inset:          0,
  background:     'rgba(0,0,0,0.45)',
  display:        'flex',
  alignItems:     'flex-end',
  justifyContent: 'center',
  zIndex:         100,
};

const dialogBox: React.CSSProperties = {
  background:   '#ffffff',
  borderRadius: '20px 20px 0 0',
  padding:      '24px 20px max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 36px)',
  width:        '100%',
  maxWidth:     '430px',
};

const msgBtn: React.CSSProperties = {
  display:      'block',
  width:        '100%',
  textAlign:    'start',
  padding:      '14px 16px',
  borderRadius: '12px',
  border:       '1px solid rgba(43,52,55,0.1)',
  background:   '#ffffff',
  cursor:       'pointer',
  marginBottom: '8px',
};
